/**
 * `@npc/copy` —— 文案模板的渲染层，TS 这一端。
 *
 * # 为什么有这个包
 *
 * PRD §8 把「AI 的人话说错了」列成风险——把「日志清理」说成「数据清理」会把人吓一跳。
 * 对策不是叮嘱模型小心，是让它在物理上没有别的选择：所有用户可见的句子都来自
 * `docs/copy/*.yaml`，模型只能挑一个模板 id、填几个变量。
 *
 * RFC-002 §1.4 把这条推到了方案对象上：AI 产出的对象里没有任何自由文本字段，
 * 只有 `reason_code` 与 `reason_vars`。这个包就是 `reason_code` 变成句子的那一步。
 *
 * # 所以这个包刻意不提供什么
 *
 * 没有任何拼句子的入口。想让用户看到一句话，只有 `render` 与 `renderDynamic` 两条路。
 *
 * # 三道关
 *
 * | 模板 id 写错 | 编译期。不在 `CopyId` 联合类型里 |
 * | 变量漏传多传 | 编译期。漏传是缺属性，多传是对象字面量的多余属性检查 |
 * | 变量值不干净 | 运行期。emoji、术语黑名单、换行、长度上限，任一不过就报错 |
 *
 * 第三道关是运行期的，因为值本来就是运行期才有的。它 fail closed：
 * 宁可报错让上层降级，也不渲染出半句话给用户看。
 *
 * # 用法
 *
 * ```ts
 * render('host.status.online')
 * render('host.summary.backend_and_db', { project_name: '小食堂点单', who_uses: '客人下单' })
 *
 * // 给规则引擎与 AI 的 reason_code 用。id 不存在就 ok: false
 * const r = renderDynamic(reasonCode, reasonVars)
 * ```
 *
 * 与 Go 那端（`packages/shared-go/copytext`）**逐字节一致**，
 * 由 `packages/copy/src/crosscheck.test.ts` 与 Go 侧的同名测试各自对着同一份
 * `testdata/expected.txt` 验。
 *
 * 生成物由 `tools/gen-copy.mjs` 产出，跑 `make generate-copy`。
 */
import { BANNED_TERMS, CJK_RANGES, EMOJI_RANGES, MAX_VALUE_RUNES, MESSAGES } from './rules.gen'
import { COPY_IDS, TEMPLATES, TEMPLATES_EN } from './templates.gen'
import type { CopyId, CopyVarMap, VarlessCopyId } from './templates.gen'
import type { Finding, RenderResult, Template } from './types'

export type { CopyId, CopyVarMap, VarlessCopyId } from './templates.gen'
export type { Finding, RenderResult, Template } from './types'
export { COPY_IDS, TEMPLATES, TEMPLATES_EN } from './templates.gen'
export { MAX_VALUE_RUNES } from './rules.gen'

/** 渲染没过。message 与 Go 那端的 error 字符串逐字一致。 */
export class CopyError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CopyError'
  }
}

const PLACEHOLDER = /\{([a-z0-9_]+)\}/g
const POSITIONAL = /\{(\d+)\}/g

/**
 * 把 {0} {1} 按位置替换。
 *
 * 刻意不用模板字符串：两端的报错文案要逐字节一致，就不能让各自语言的
 * 格式化差异插进来。越界的占位符原样留着。
 */
function format(text: string, ...args: readonly string[]): string {
  POSITIONAL.lastIndex = 0
  return text.replace(POSITIONAL, (whole, digits: string) => {
    const v = args[Number(digits)]
    return v === undefined ? whole : v
  })
}

function inRanges(ranges: ReadonlyArray<readonly [number, number]>, cp: number): boolean {
  let lo = 0
  let hi = ranges.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const range = ranges[mid]
    if (range === undefined) return false
    if (cp < range[0]) hi = mid - 1
    else if (cp > range[1]) lo = mid + 1
    else return true
  }
  return false
}

/**
 * 一段文字里有没有 emoji。
 *
 * 判据是生成时定死的码点表，不是 `/\p{Extended_Pictographic}/u`：
 * Go 那端的 regexp 不认这个类，两端要逐字节一致，判据就不能各用各的。
 */
export function hasEmoji(text: string): boolean {
  for (const ch of text) {
    if (inRanges(EMOJI_RANGES, ch.codePointAt(0) ?? 0)) return true
  }
  return false
}

function isCJK(cp: number): boolean {
  return inRanges(CJK_RANGES, cp)
}

function isASCIIDigit(cp: number): boolean {
  return cp >= 0x30 && cp <= 0x39
}

function isASCIILetter(cp: number): boolean {
  return (cp >= 0x41 && cp <= 0x5a) || (cp >= 0x61 && cp <= 0x7a)
}

/** 找「该类字符紧贴中文」的第一处，两个方向都算，返回那两个字。 */
function findAdjacent(text: string, is: (cp: number) => boolean): string | null {
  const chars = [...text]
  for (let i = 0; i + 1 < chars.length; i++) {
    const a = chars[i]
    const b = chars[i + 1]
    if (a === undefined || b === undefined) continue
    const ca = a.codePointAt(0) ?? 0
    const cb = b.codePointAt(0) ?? 0
    if ((is(ca) && isCJK(cb)) || (isCJK(ca) && is(cb))) return a + b
  }
  return null
}

/**
 * 校验一个变量值。没问题返回 null。
 *
 * 这里刻意不查「数字与中文之间的空格」——那是写模板时的规矩，由 copy-lint 在构建期把关。
 * 变量值里装的是项目名、机器名这类专有名词，「T3出行」是人家的名字，不该被我们拒掉。
 *
 * 查的五样按顺序：空、控制符、超长、emoji、术语黑名单。只报第一个。
 */
export function checkValue(name: string, value: string): string | null {
  if (value.trim() === '') return format(MESSAGES.emptyValue, name)
  for (const ch of value) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp < 0x20 || cp === 0x7f) return format(MESSAGES.controlChar, name)
  }
  const n = [...value].length
  if (n > MAX_VALUE_RUNES) return format(MESSAGES.tooLong, name, String(n), String(MAX_VALUE_RUNES))
  if (hasEmoji(value)) return format(MESSAGES.valueEmoji, name)
  for (const [term, instead] of BANNED_TERMS) {
    if (value.includes(term)) return format(MESSAGES.valueBannedTerm, name, term, instead)
  }
  return null
}

/**
 * 按 copy-lint 的四条规则查一整句话：emoji、术语黑名单、
 * 数字与中文之间的空格、英文与中文之间的空格。
 *
 * 渲染路径用不到它——模板在构建期已经过了同一批规则。它是给需要
 * 自己确认一句话干不干净的地方用的，比如把外部来的文本放进界面之前。
 */
export function checkText(text: string): Finding[] {
  const out: Finding[] = []
  if (hasEmoji(text)) out.push({ rule: 'emoji', detail: MESSAGES.emoji })
  for (const [term, instead] of BANNED_TERMS) {
    if (text.includes(term)) out.push({ rule: 'banned-term', detail: format(MESSAGES.bannedTerm, term, instead) })
  }
  const digit = findAdjacent(text, isASCIIDigit)
  if (digit !== null) out.push({ rule: 'digit-spacing', detail: format(MESSAGES.digitSpacing, digit) })
  const latin = findAdjacent(text, isASCIILetter)
  if (latin !== null) out.push({ rule: 'latin-spacing', detail: format(MESSAGES.latinSpacing, latin) })
  return out
}

/** 取一条模板。id 不存在返回 undefined。 */
export function lookup(id: string): Template | undefined {
  return Object.prototype.hasOwnProperty.call(TEMPLATES, id) ? TEMPLATES[id as CopyId] : undefined
}

/**
 * 把 {名字} 换成变量值，**单遍**。
 *
 * 单遍是要紧的：替换进去的值里如果碰巧有 {别的名字}，不会被二次展开。
 * 否则变量值就成了一条能改写模板的通道，前面三道关全白设。
 */
function substitute(text: string, vars: Readonly<Record<string, string>>): string {
  PLACEHOLDER.lastIndex = 0
  return text.replace(PLACEHOLDER, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? (vars[name] as string) : whole,
  )
}

/**
 * 动态渲染。规则引擎的 `reason_code`、AI 的 `reason_vars` 走这里。
 *
 * 会失败的四种情况，顺序是定死的（Go 那端必须报同一个错）：
 * id 不存在、少了变量、多了变量、某个变量的值没过校验。
 */
/**
 * 当前文案语言（L1，2026-08-29）。默认中文；英文档查不到的 id 自动回退中文。
 * 后端渲染的文案（错误 user_message 等）不走这里，仍是中文——那是 L2 的事。
 */
export type CopyLocale = 'zh' | 'en'

let activeLocale: CopyLocale = 'zh'

/** 切语言。应用层在启动时按用户偏好调一次，切换器里再调。 */
export function setCopyLocale(locale: CopyLocale): void {
  activeLocale = locale
}

/** 现在用的是哪档。 */
export function copyLocale(): CopyLocale {
  return activeLocale
}

function textFor(id: string, tpl: Template): string {
  if (activeLocale === 'en') {
    const en = TEMPLATES_EN[id as keyof typeof TEMPLATES_EN]
    if (typeof en === 'string') return en
  }
  return tpl.text
}

export function renderDynamic(id: string, vars: Readonly<Record<string, string>> = {}): RenderResult {
  const tpl = lookup(id)
  if (tpl === undefined) return { ok: false, error: format(MESSAGES.unknownTemplate, id) }

  for (const name of tpl.vars) {
    if (!Object.prototype.hasOwnProperty.call(vars, name)) {
      return { ok: false, error: format(MESSAGES.missingVar, id, name) }
    }
  }
  for (const name of Object.keys(vars).sort()) {
    if (!tpl.vars.includes(name)) return { ok: false, error: format(MESSAGES.unexpectedVar, id, name) }
  }
  for (const name of tpl.vars) {
    const bad = checkValue(name, vars[name] as string)
    if (bad !== null) return { ok: false, error: bad }
  }

  return { ok: true, text: substitute(textFor(id, tpl), vars) }
}

/**
 * 类型化渲染。id 与变量都在编译期定死，值没过校验时抛 {@link CopyError}。
 *
 * 界面上用这个。要在失败时降级而不是炸掉的地方，用 {@link renderDynamic}。
 */
export function render<K extends VarlessCopyId>(id: K): string
export function render<K extends CopyId>(id: K, vars: CopyVarMap[K]): string
export function render(id: CopyId, vars?: Readonly<Record<string, string>>): string {
  const r = renderDynamic(id, vars ?? {})
  if (!r.ok) throw new CopyError(r.error)
  return r.text
}

/** 全部模板 id，按字母序。 */
export function ids(): readonly CopyId[] {
  return COPY_IDS
}
